import { useEffect } from 'react';

interface SeoOptions {
    title: string;
    description: string;
    canonical: string;
    /** JSON-LD object injected as <script type="application/ld+json">. */
    jsonLd?: object;
    type?: 'website' | 'article';
    publishedTime?: string;
}

/**
 * Per-route <head> management for the SPA.
 *
 * podus.org has no Helmet, so routes set their own head tags imperatively. The prerender step boots
 * the real app in headless Chrome and snapshots the resulting DOM, so whatever this writes ends up
 * in the static HTML a crawler receives. On unmount every tag is reverted to what it was, so client
 * navigation between routes stays correct.
 */
export function useSeo({ title, description, canonical, jsonLd, type = 'website', publishedTime }: SeoOptions) {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;

        // Upsert a meta/link tag, remembering the previous value (or that it did not exist).
        const touched: Array<() => void> = [];
        const setMeta = (selector: string, create: () => HTMLElement, value: string, attr: string) => {
            let el = document.head.querySelector<HTMLElement>(selector);
            if (el) {
                const prev = el.getAttribute(attr);
                el.setAttribute(attr, value);
                touched.push(() => (prev === null ? el!.removeAttribute(attr) : el!.setAttribute(attr, prev)));
            } else {
                el = create();
                el.setAttribute(attr, value);
                document.head.appendChild(el);
                const node = el;
                touched.push(() => node.remove());
            }
        };

        const meta = (name: string, content: string, attrName: 'name' | 'property' = 'name') =>
            setMeta(
                `meta[${attrName}="${name}"]`,
                () => {
                    const m = document.createElement('meta');
                    m.setAttribute(attrName, name);
                    return m;
                },
                content,
                'content',
            );

        meta('description', description);
        meta('og:title', title, 'property');
        meta('og:description', description, 'property');
        meta('og:url', canonical, 'property');
        meta('og:type', type, 'property');
        meta('twitter:title', title);
        meta('twitter:description', description);
        if (publishedTime) meta('article:published_time', publishedTime, 'property');

        setMeta(
            'link[rel="canonical"]',
            () => {
                const l = document.createElement('link');
                l.setAttribute('rel', 'canonical');
                return l;
            },
            canonical,
            'href',
        );

        // The static index.html carries homepage-only structured data (e.g. FAQPage). Any route that
        // manages its own head is not the homepage, so detach it here and restore it on unmount —
        // FAQPage schema must not appear on a page without the matching visible FAQ.
        const homeOnly = [...document.head.querySelectorAll<HTMLElement>('[data-home-only]')];
        homeOnly.forEach((el) => el.remove());

        let script: HTMLScriptElement | null = null;
        if (jsonLd) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-route-jsonld', 'true');
            script.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }

        return () => {
            document.title = prevTitle;
            touched.forEach((revert) => revert());
            script?.remove();
            homeOnly.forEach((el) => document.head.appendChild(el));
        };
    }, [title, description, canonical, type, publishedTime, jsonLd]);
}
